import React from 'react';
import { API_BASE_URL, DEBUG_MODE } from './constants';

const DebugPanel = React.memo(function DebugPanel({
  debugMode,
  setDebugMode,
  currentStep,
  userType,
  savedPatientId,
  existingPatientResult,
  nearestLocations,
  testServerConnection,
  lastApiResponse,
  debugLog,
}) {
  if (!debugMode) return null;

  return (
    <div className="mb-6 bg-gray-50 border border-gray-200 rounded-lg">
      <details className="group">
        <summary className="cursor-pointer p-4 hover:bg-gray-100 transition-colors">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold text-gray-700 inline-flex items-center">
              🔍 Debug Panel
              <svg className="w-4 h-4 ml-2 transform group-open:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </h4>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setDebugMode(false);
              }}
              className="text-xs text-gray-500 hover:text-gray-700"
            >
              Hide Debug
            </button>
          </div>
        </summary>
        <div className="p-4 pt-0">
          <div className="space-y-3">
            <div className="text-xs">
              <strong>Current Step:</strong> {currentStep} | <strong>User Type:</strong> {userType || 'Not selected'} | <strong>Patient ID:</strong> {savedPatientId || 'None'}
              {existingPatientResult && (
                <div className="mt-1 text-green-600">
                  <strong>Existing Patient:</strong> {existingPatientResult.FirstName} {existingPatientResult.LastName} (ID: {existingPatientResult.PatientId})
                </div>
              )}
              {nearestLocations.length > 0 && (
                <div className="mt-1 text-blue-600">
                  <strong>Filtered Locations:</strong> {nearestLocations.length} locations within 30 miles
                  <div className="text-[10px] mt-1">
                    {nearestLocations.map(loc => `${loc.name} (${loc.distance ? loc.distance.toFixed(1) + ' mi' : 'Virtual'})`).join(', ')}
                  </div>
                </div>
              )}
              {nearestLocations.length === 0 && currentStep === 3 && (
                <div className="mt-1 text-red-600">
                  <strong>⚠️ No filtered locations!</strong> Showing all locations as fallback
                </div>
              )}
            </div>

            <div className="flex space-x-2">
              <button
                onClick={async () => {
                  const isConnected = await testServerConnection();
                  if (isConnected) {
                    alert('✅ Server is running and accessible');
                  } else {
                    alert('❌ Server connection failed - check if backend is running on ' + API_BASE_URL);
                  }
                }}
                className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
              >
                Test Server
              </button>
              <button
                onClick={() => {
                  const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
                  const endpoint = '/existing-patient/search';
                  const finalUrl = `${baseUrl}${endpoint}`;
                  if (DEBUG_MODE) {
                    console.log('🔧 [URL TEST] Expected:', 'http://127.0.0.1:8000/existing-patient/search');
                    console.log('🔧 [URL TEST] Actual:  ', finalUrl);
                  }
                  alert(`URL Test:\nExpected: http://127.0.0.1:8000/existing-patient/search\nActual: ${finalUrl}\nMatch: ${finalUrl === 'http://127.0.0.1:8000/existing-patient/search'}`);
                }}
                className="px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded hover:bg-purple-200"
              >
                Test URL
              </button>
              <button
                onClick={() => {
                  const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
                  const endpoint = '/existing-patient/search';
                  const finalUrl = `${baseUrl}${endpoint}`;
                  if (DEBUG_MODE) {
                    console.log('🔍 [DEBUG] Current API Base URL:', API_BASE_URL);
                    console.log('🔍 [DEBUG] Clean Base URL:', baseUrl);
                    console.log('🔍 [DEBUG] Final Search Patient URL:', finalUrl);
                  }
                }}
                className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200"
              >
                Log URLs
              </button>
            </div>

            {lastApiResponse && (
              <details className="text-xs">
                <summary className="cursor-pointer text-blue-600">Last API Response</summary>
                <pre className="mt-2 bg-white p-2 rounded border overflow-x-auto">
                  {JSON.stringify(lastApiResponse, null, 2)}
                </pre>
              </details>
            )}

            {debugLog.length > 0 && (
              <details className="text-xs">
                <summary className="cursor-pointer text-green-600">Debug Log ({debugLog.length} entries)</summary>
                <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                  {debugLog.slice(-5).map((log, index) => (
                    <div key={index} className="bg-white p-2 rounded border">
                      <div className="font-medium">{log.action}</div>
                      <div className="text-gray-500">{new Date(log.timestamp).toLocaleTimeString()}</div>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
        </div>
      </details>
    </div>
  );
});

export default DebugPanel;
